import React, {useEffect, useMemo, useState} from 'react';
import {Box, Tooltip} from "@mui/material";
import {IconButton, Tab, TabList, Tabs, Typography, CircularProgress, useTheme} from "@mui/joy";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {
    CloseRounded,
    MoreVertRounded,
    NotificationsActiveRounded,
    NotificationAddRounded,
    OpenInNewRounded,
} from "@mui-symbols-material/w300";
import {useKeycloak} from "@react-keycloak/web";
import {ActionsMenu, getInstalledBaseTypeIcon, getUserResources, useUserResources} from "@dreebit-registry/vsm-microservice-components";
import {ActionItemConfig} from "@dreebit-registry/vsm-microservice-components/dist/components/ActionsMenu";
import {InstalledBaseNode} from "../../codegen/graphql";
import {InstalledBaseNodeDetail} from "../../graphql/queries/installedBaseNodeDetail";
import {InstalledBaseNodeSubscriptions} from "../../graphql/queries/installedBaseNodeSubscriptions";
import {useNodeActions} from "../InstalledBaseTreeColumn/nodeActions";
import {getNodeDetailRoute} from "../../utils/getNodeShortcutTarget";
import {MoveInstalledBaseNodeSuccessInfo} from "../MoveInstalledBaseNodeModal";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";
import {buildInstalledBasePathString} from "../../utils/installedBasePath";
import {DETAIL_TABS, DetailTab} from "../../utils/installedBaseUrl";
import {isRiskReportsEnabled} from "../../utils/riskReportsEnabled";
import {isSlaEnabled} from "../../utils/slaEnabled";
import {InstalledBaseNodeNetworkScan as InstalledBaseNodeNetworkScanQuery} from "../../graphql/queries/installedBaseNodeNetworkScan";
import {getNetworkScanColor, getNetworkScanStateLabel} from "../../config/networkScanConfig";
import {resolveNodeDisplayImage} from "../../utils/nodeDisplayImage";
import AssetProductImage from "../AssetProductImage";
import InstalledBaseNodeContacts from "../InstalledBaseNodeContacts";
import InstalledBaseNodeFileManagement from "../InstalledBaseNodeFileManagement";
import InstalledBaseNodeInformation from "../InstalledBaseNodeInformation";
import InstalledBaseNodeNetworkScan from "../InstalledBaseNodeNetworkScan";
import InstalledBaseNodeRiskReports from "../InstalledBaseNodeRiskReports";
import NodeFavoriteButton from "../NodeFavoriteButton";
import NodeSubscriptionModal from "../NodeSubscriptionModal";

// Height of title row (36) + type row (9) + path row (14) — the tile squares
// off the full text block, keeping the 113px header budget intact.
const HEADER_TILE_SIZE = 59;

export const NODE_DETAIL_PANEL_WIDTH = 900;

interface Props {
    nodeId: string;
    activeTab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
    onClose: () => void;
    isRoot: boolean;
    deleteNodeCallback?: (deletedNodeId: string) => void;
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
}

const InstalledBaseNodeDetailPanel = ({
    nodeId,
    activeTab,
    onTabChange,
    onClose,
    isRoot,
    deleteNodeCallback,
    moveNodeCallback,
}: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const resources = useUserResources();
    const {keycloak} = useKeycloak();

    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

    const {data, loading, error, refetch} = useQuery(InstalledBaseNodeDetail, {
        variables: {id: nodeId},
        fetchPolicy: "cache-and-network",
    });
    const node = data?.installedBaseNode as InstalledBaseNode | undefined;

    // Network scan tab (IBM-1039) is data-driven: offered where the user may
    // document a scan, where one is documented, or where an effective scan
    // applies (systems / assets) — client root included, no type list.
    // Separate query so the flag can skip it entirely.
    const networkScanQuery = useQuery(InstalledBaseNodeNetworkScanQuery, {
        variables: {id: nodeId},
        skip: !isSlaEnabled(),
        fetchPolicy: 'cache-and-network',
    });
    const networkScanNode = networkScanQuery.data?.installedBaseNode;
    const networkScanSettled = !isSlaEnabled() || !!networkScanQuery.data || (!networkScanQuery.loading && networkScanQuery.called);
    const showNetworkScanTab = isSlaEnabled() && !!networkScanNode && (
        !!networkScanNode.canDocumentNetworkScan
        || !!networkScanNode.networkScan
        || !!networkScanNode.assetLocation?.effectiveNetworkScan
        || !!networkScanNode.system?.effectiveNetworkScan
    );
    // Traffic-light dot on the tab: what the card shows — the own scan, else the
    // effective one (systems / assets). No dot while nothing is documented.
    const networkScanState = networkScanNode?.networkScan?.state
        ?? networkScanNode?.assetLocation?.effectiveNetworkScan?.state
        ?? networkScanNode?.system?.effectiveNetworkScan?.state
        ?? null;
    // Deep link to a node without the tab: fall back once the query settled —
    // never while loading, or the tab would flicker to Information on open.
    useEffect(() => {
        if (activeTab === 'networkScan' && networkScanSettled && !showNetworkScanTab) {
            onTabChange('information');
        }
    }, [activeTab, networkScanSettled, showNetworkScanTab, onTabChange]);

    const typeName = getInstalledBaseNodeTypeName(node?.nodeType, t);
    const pathString = buildInstalledBasePathString(node);
    const assetLocationId = node?.assetLocation?.id;
    const systemId = node?.system?.id;
    // User-set node image → product image / system-type PNG → placeholder. Assets
    // and systems always get an image tile (branded placeholder when none); other
    // node types show their tinted type icon unless the user set an image.
    const {src: tileImage, source: tileImageSource} = resolveNodeDisplayImage(node);
    const showImageTile = tileImageSource !== 'none' || !!assetLocationId || !!systemId;
    const detailRoute = node ? getNodeDetailRoute(node) : undefined;

    // Client root: the subscription modal additionally manages the client's
    // IoT report subscription (same derivation as in nodeActions.tsx).
    const iotReportClientId =
        node?.parentNode == null && node?.owner?.id ? String(node.owner.id) : undefined;

    const {data: subscriptionsData} = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: 'cache-first',
    });
    const isAlreadySubscribed = useMemo(() => {
        const subs = (subscriptionsData?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[];
        return subs.some((sub) => sub?.installedBaseNode?.id === nodeId);
    }, [subscriptionsData, nodeId]);

    // Same write rule as the kebab's Edit action (nodeActions.tsx): client
    // roots fall back to the keycloak resource instead of node access.
    const canWriteNode = (isRoot && resources.includes('installedBaseRootNodeUpdate'))
        || (!isRoot && !!node?.access?.write);

    // Contact management is gated on node write access PLUS the
    // administrateContactPersons resource, which ships in the otto_crm bucket
    // of the JWT (not otto_installed_base).
    const canWriteContacts = !!node?.access?.write
        && getUserResources(keycloak.token, 'otto_crm').includes('administrateContactPersons');

    const {items: actionItems, modals} = useNodeActions({
        targetNode: node as InstalledBaseNode,
        isRoot,
        context: 'detailPanel',
        refetchFunction: refetch,
        deleteNodeCallback,
        moveNodeCallback,
        // The Information tab embeds the edit form — no modal from the panel.
        onEditNode: () => onTabChange('information'),
    });

    const openDetailsItem: ActionItemConfig | null = detailRoute ? {
        item: (
            <Box display="flex" alignItems="center">
                <OpenInNewRounded fontSize="inherit" sx={{mr: '4px'}}/>
                {assetLocationId ? t('Open asset details') : t('Open system details')}
            </Box>
        ),
        onClick: () => navigate(detailRoute),
        active: true,
    } : null;
    const menuItems = openDetailsItem ? [openDetailsItem, ...actionItems] : actionItems;

    const tabLabels: Record<DetailTab, string> = {
        information: t('Information'),
        contact: t('Contact'),
        documents: t('Documents'),
        riskReports: t('Risk assessment reports'),
        networkScan: t('Network scan'),
    };

    // riskReports is feature-flagged (testing/local only while the backend is
    // in development); the URL contract keeps the tab id valid regardless.
    const visibleTabs = DETAIL_TABS.filter((tab) =>
        (tab !== 'riskReports' || isRiskReportsEnabled())
        && (tab !== 'networkScan' || showNetworkScanTab),
    );

    return (
        <Box
            data-testid="node-detail-panel"
            sx={{
                minWidth: `${NODE_DETAIL_PANEL_WIDTH}px`,
                maxWidth: `${NODE_DETAIL_PANEL_WIDTH}px`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
            }}
        >
            {/* Header — mirrors the column header layout (header.tsx):
                pt 4 + title row 36 + type row 9 (14 - 5 mt) + path row 14
                + tabs mt 6 + tabs 44 (size lg incl. 2px border) = 113px,
                the column header's total height, so the tab bottom border
                aligns with the neighbouring column's first-row border. */}
            <Box sx={{flexShrink: 0, p: "4px 20px 0 20px", display: 'flex', gap: '12px'}} className={"full-width"}>
                {/* Square tile spanning title + both subtitle rows: product image
                    for assets, system-type image for systems, tinted nodeType
                    icon for everything else (artifact-mockup look). */}
                <Box
                    data-testid="node-detail-tile"
                    sx={{
                        width: `${HEADER_TILE_SIZE}px`,
                        height: `${HEADER_TILE_SIZE}px`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // primary.100 / primary.700 — hardcoded like the other tonal
                        // surfaces in the app (e.g. InstalledBaseEmptyView).
                        backgroundColor: showImageTile ? 'transparent' : '#FDDDCD',
                    }}
                >
                    {node && (
                        showImageTile ? (
                            <AssetProductImage
                                src={tileImage}
                                industryType={node.owner?.industryType}
                                style={{width: HEADER_TILE_SIZE, height: HEADER_TILE_SIZE, objectFit: 'contain', borderRadius: 12}}
                            />
                        ) : (
                            getInstalledBaseTypeIcon(node.nodeType?.key, 30, '#BD4200')
                        )
                    )}
                </Box>

                <Box className={"flex-col"} sx={{flex: 1, minWidth: 0}}>
                <Box
                    className={"flex-row full-width"}
                    sx={{justifyContent: "space-between", alignItems: "center", minHeight: "2.25rem"}}
                >
                    <Box className={"flex-row"} sx={{alignItems: "center", overflow: "hidden"}}>
                        {detailRoute ? (
                            <Tooltip
                                title={assetLocationId ? t('Open asset details') : t('Open system details')}
                                placement={'top-start'} enterDelay={700} enterNextDelay={500}
                            >
                                <Box
                                    className={"flex-row"}
                                    role="link"
                                    tabIndex={0}
                                    onClick={() => navigate(detailRoute)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(detailRoute); }}
                                    sx={{
                                        alignItems: "center",
                                        gap: "6px",
                                        overflow: "hidden",
                                        cursor: "pointer",
                                        '&:hover .node-detail-title': {textDecoration: 'underline'},
                                    }}
                                >
                                    <Typography level={'titleMedium'} noWrap className="node-detail-title">
                                        {node?.title}
                                    </Typography>
                                    <OpenInNewRounded sx={{fontSize: 16, color: '#666666', flexShrink: 0}}/>
                                </Box>
                            </Tooltip>
                        ) : (
                            <Tooltip title={node?.title ?? ''} placement={'top-start'}
                                     enterDelay={700} enterNextDelay={500}>
                                <Typography level={'titleMedium'} noWrap>
                                    {node?.title}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>

                    <Box className={"flex-row"} sx={{alignItems: "center", flexShrink: 0}}>
                        {node && <NodeFavoriteButton nodeId={nodeId} isStarred={!!node.isStarred}/>}
                        <IconButton
                            aria-label={isAlreadySubscribed ? t('Edit subscription') : t('Subscribe')}
                            title={isAlreadySubscribed ? t('Edit subscription') : t('Subscribe')}
                            onClick={() => setSubscriptionModalOpen(true)}
                        >
                            {isAlreadySubscribed
                                ? <NotificationsActiveRounded fontSize={'small'}/>
                                : <NotificationAddRounded fontSize={'small'}/>
                            }
                        </IconButton>
                        <ActionsMenu
                            items={menuItems}
                            icon={<MoreVertRounded fontSize={'small'}/>}
                        />
                        <IconButton
                            aria-label={t('Close')}
                            title={t('Close')}
                            onClick={onClose}
                            data-testid="node-detail-panel-close"
                        >
                            <CloseRounded fontSize={'small'}/>
                        </IconButton>
                    </Box>
                </Box>

                {/* Node type — same caption style as the column header subtitle
                    (the tile carries the type icon now) */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: '4px', minHeight: '14px', mt: '-5px'}}>
                    {node &&
                        <Typography level={'caption'} noWrap sx={{color: '#666666'}}>
                            {typeName}
                        </Typography>
                    }
                </Box>

                {/* Path of the node (ancestors only). Height reserved for root nodes. */}
                <Box sx={{display: 'flex', alignItems: 'center', minHeight: '14px'}}>
                    {pathString &&
                        <Tooltip title={pathString} placement={'top-start'} enterDelay={700} enterNextDelay={500}>
                            <Typography level={'caption'} noWrap sx={{color: '#666666'}} data-testid="node-detail-path">
                                {pathString}
                            </Typography>
                        </Tooltip>
                    }
                </Box>
                </Box>
            </Box>

            {/* Tabs — text-only tabs; the active one gets primary color and a
                primary underline resting on the full-width divider below. The
                divider is the header/content separator and sits in the same
                pixel band as the neighbouring column's first-row borderTop. */}
            <Tabs
                value={activeTab}
                onChange={(_, value) => onTabChange(value as DetailTab)}
                size={"lg"}
                sx={{
                    flexShrink: 0,
                    mt: "6px",
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <TabList disableUnderline sx={{px: "20px", gap: "12px"}}>
                    {visibleTabs.map((tab) => (
                        <Tab
                            key={tab}
                            value={tab}
                            disableIndicator
                            variant="plain"
                            color="neutral"
                            sx={{
                                // bodyBold (14px/700) — the project's small bold
                                // style, matching the mockup's compact tab labels.
                                fontSize: theme.typography.bodyBold.fontSize,
                                fontWeight: theme.typography.bodyBold.fontWeight,
                                flexShrink: 0,
                                textTransform: 'none',
                                minWidth: 'unset',
                                px: '6px',
                                borderBottom:
                                    activeTab === tab
                                        ? `2px solid ${theme.vars.palette.primary[500]}`
                                        : '2px solid transparent',
                                backgroundColor: 'transparent',
                                '&[aria-selected="true"]': {
                                    backgroundColor: 'transparent',
                                    color: theme.vars.palette.primary[500],
                                },
                                '&:not([aria-selected="true"]):hover': {backgroundColor: 'transparent'},
                            }}
                        >
                            {tab === 'networkScan' && networkScanState && (
                                <Box
                                    component="span"
                                    data-testid="network-scan-tab-indicator"
                                    data-state={networkScanState}
                                    title={getNetworkScanStateLabel(networkScanState, t)}
                                    sx={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        mr: '6px',
                                        flexShrink: 0,
                                        backgroundColor: getNetworkScanColor(networkScanState),
                                    }}
                                />
                            )}
                            {tabLabels[tab]}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {/* Body */}
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex'}}>
                {loading && !node ? (
                    <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress size="sm"/>
                    </Box>
                ) : error && !node ? (
                    <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3}}>
                        <Typography level="body-md" sx={{color: 'text.secondary', textAlign: 'center'}}>
                            {t('The node could not be loaded.')}
                        </Typography>
                    </Box>
                ) : activeTab === 'information' ? (
                    <InstalledBaseNodeInformation
                        nodeId={nodeId}
                        canWrite={canWriteNode}
                        onOpenDocuments={() => onTabChange('documents')}
                        onNodeUpdated={refetch}
                    />
                ) : activeTab === 'contact' ? (
                    <InstalledBaseNodeContacts
                        nodeId={nodeId}
                        ownerId={node?.owner?.id ? String(node.owner.id) : undefined}
                        canWrite={canWriteContacts}
                    />
                ) : activeTab === 'documents' ? (
                    <InstalledBaseNodeFileManagement nodeId={nodeId} canWrite={!!node?.access?.write}/>
                ) : activeTab === 'riskReports' && isRiskReportsEnabled() ? (
                    <InstalledBaseNodeRiskReports nodeId={nodeId}/>
                ) : activeTab === 'networkScan' && !networkScanSettled ? (
                    <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress size="sm"/>
                    </Box>
                ) : activeTab === 'networkScan' && showNetworkScanTab ? (
                    <InstalledBaseNodeNetworkScan
                        nodeId={nodeId}
                        nodeTypeKey={node?.nodeType?.key}
                        pathNodeIds={(node?.pathNodes ?? []).map((n: any) => n?.id).filter(Boolean)}
                        assetLocationId={assetLocationId}
                        systemId={systemId}
                        node={node}
                    />
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            p: 3,
                        }}
                    >
                        <Typography level="title-md">{tabLabels[activeTab]}</Typography>
                        <Typography level="body-sm" sx={{color: 'text.secondary', textAlign: 'center'}}>
                            {t('Content will follow soon')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {modals}
            {subscriptionModalOpen &&
                <NodeSubscriptionModal
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    installedBaseNodeId={nodeId}
                    iotReportClientId={iotReportClientId}
                />
            }
        </Box>
    );
};

export default InstalledBaseNodeDetailPanel;
